import { Feature } from 'ol';
import { Fill, Stroke, Style, Circle } from 'ol/style';
import { Vector as VectorLayer, VectorImage } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { GeoJSON } from 'ol/format';

import { getMarkerStyle } from '@/assets/js/stylesUtils';
import { getFeatureCollection } from '@/assets/js/mapUtils';
import { getContext } from '@/assets/js/mapContext';

export default {
  methods: {
    clearCustomLayer(id, group) {
      const layer = this.getLayerById(id, group);
      if (!layer) return;
      layer.getSource().clear();
    },
    customLayerAddFeatures(features, id, group) {
      const layer = this.getLayerById(id, group);
      if (!layer) return;
      layer.getSource().addFeatures(features);
    },
    getCustomLayerStyle(layerParams) {
      if (layerParams.style)
        return (feature, resolution) =>
          [
            ...(typeof layerParams.style === 'function'
              ? [layerParams.style(feature, this.map.getView().getZoomForResolution(resolution))].flat()
              : [layerParams.style].flat()),
            ...(layerParams.drawPointMarker ? [getMarkerStyle()] : []),
          ].filter(s => !!s);
      if (layerParams.defaultOlStyle) return;
      return new Style({
        fill: new Fill({
          color: 'rgba(200, 0, 0, 0.6)',
        }),
        stroke: new Stroke({
          color: 'rgba(255, 0, 0, 1)',
          width: 2,
        }),
        image: new Circle({
          radius: 5,
          stroke: new Stroke({
            color: 'rgba(255, 0, 0, 1)',
          }),
          fill: new Fill({
            color: 'rgba(200, 0, 0, 0.6)',
          }),
        }),
      });
    },
    getCustomLayerGeometryFeatureCollection(data) {
      if (!data) return;

      /** FeatureCollection */
      if (data.type === 'FeatureCollection') return data;

      /** Single Feature */
      if (data.type === 'Feature') return getFeatureCollection([data], data?.crs);

      /** Array of Features */
      if (Array.isArray(data)) return getFeatureCollection(data, data[0]?.crs);

      /** GeometryCollection */
      if (data.type === 'GeometryCollection')
        return getFeatureCollection(
          data.geometries.map(geometry => ({
            type: 'Feature',
            geometry,
          })),
          data?.crs
        );

      /** Single geometry */
      if (['Polygon', 'MultiPolygon', 'LineString', 'MultiLineString', 'Point', 'MultiPoint'].includes(data.type))
        return getFeatureCollection(
          [
            {
              type: 'Feature',
              geometry: data,
            },
          ],
          data?.crs
        );

      /** If geometry is not supported - an exception is thrown. Write your own driver if needed */
      throw new Error('Provided geometry data is not supported');
    },
    getCustomLayerGeometryOlFeatures(data) {
      const featureCollection = this.getCustomLayerGeometryFeatureCollection(data);
      if (!featureCollection) return;
      if (!featureCollection?.features?.length) return [];
      return new GeoJSON().readFeatures(featureCollection, {
        featureProjection: this.$_config.defaultEpsg,
        dataProjection: featureCollection.crs?.properties?.name || this.$_config.defaultEpsg,
      });
    },
    getCustomVectorLayerInstance(id, layerParams = {}) {
      const config = {
        id,
        isSpecial: true,
        zIndex: layerParams.zIndex || 999,
        opacity: layerParams.opacity || 1,
        source: new VectorSource(),
        style: this.getCustomLayerStyle(layerParams),
        ...(layerParams.layerConfig ?? {}),
      };
      return layerParams.renderImage ? new VectorImage(config) : new VectorLayer(config);
    },
    fitViewToCustomLayer(id, group) {
      const layer = this.getLayerById(id, group);
      if (!layer) return;
      this.fitView(layer?.getSource()?.getExtent());
    },
    addCustomGeojsonLayer({ id, group, groupIndex, geometry, layerParams = {}, fitView = false }) {
      try {
        if (!id) throw Error('Layer ID not provided.');
        const features = this.getCustomLayerGeometryOlFeatures(geometry);
        if (!features) {
          if (group) this.getLayerById(group).getLayers().remove(this.getLayerById(id, group));
          else this.map.removeLayer(this.getLayerById(id, group));
          return;
        }
        if (this.getLayerById(id, group)) {
          this.clearCustomLayer(id, group);
          if (features.length) this.customLayerAddFeatures(features, id, group);
        } else {
          const newLayer = this.getCustomVectorLayerInstance(id, layerParams);
          if (features.length) newLayer.getSource().addFeatures(features);
          if (group) {
            const groupLayers = this.getLayerById(group).getLayers();
            if (groupIndex !== undefined) groupLayers.insertAt(groupIndex, newLayer);
            else groupLayers.push(newLayer);
          } else this.map.addLayer(newLayer);
        }
        if (fitView) this.fitViewToCustomLayer(id, group);
      } catch (error) {
        throw new Error(error);
      }
    },
    addCustomLayer(geometry, id, layerParams = {}, group) {
      let featureFromGeom;
      if (Array.isArray(geometry) && geometry?.[0] instanceof Feature) featureFromGeom = geometry;
      else featureFromGeom = geometry ? [new Feature({ geometry })] : [];
      if (this.getLayerById(id, group)) {
        this.clearCustomLayer(id, group);
        this.customLayerAddFeatures(featureFromGeom, id, group);
      } else {
        const newLayer = this.getCustomVectorLayerInstance(id, layerParams);
        newLayer.getSource().addFeatures(featureFromGeom);
        this.map.addLayer(newLayer);
      }
    },
    toggleCustomVectorLayerVisibility(id, value, group) {
      this.getLayerById(id, group)?.setVisible(value);
    },
  },
  mounted() {
    getContext(this).$off('toggleCustomVectorLayer');
    getContext(this).$on('toggleCustomVectorLayer', this.addCustomGeojsonLayer);
    getContext(this).$off('toggleCustomVectorLayerVisibility');
    getContext(this).$on('toggleCustomVectorLayerVisibility', this.toggleCustomVectorLayerVisibility);
    getContext(this).$off('zoomToCustomLayer');
    getContext(this).$on('zoomToCustomLayer', this.fitViewToCustomLayer);
  },
};
